/**
 * /admin/blog — admin list of imported blog posts (JWT-protected).
 *
 * Phase 2L-A51 — adds bulk-publish-selected-drafts:
 *   • Checkboxes on draft rows only (published rows are immutable here
 *     beyond the existing per-row toggle).
 *   • "Select all visible drafts" master toggle.
 *   • Confirmation modal before publishing, with explicit warning that
 *     posts with banned-phrase warnings will stay draft.
 *   • Summary toast after the bulk action so the admin sees which
 *     posts published vs. were skipped (and why).
 */
import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import AutoSEOWebhookDiagnostics from "@/components/AutoSEOWebhookDiagnostics";

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("en-AU", { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
}

export default function AdminBlog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [confirming, setConfirming] = useState(false);
  const [publishing, setPublishing] = useState(false);
  // Phase 2L-A69 — explicit Draft / Published / All filter so admins can
  // confirm freshly-arrived GetAutoSEO drafts (status="draft") at a
  // glance, even when the list grows long.
  const [statusFilter, setStatusFilter] = useState("all");
  const [totals, setTotals] = useState({ all: 0, draft: 0, published: 0 });

  const load = async () => {
    setLoading(true);
    try {
      const qs = statusFilter === "all" ? "" : `&status=${statusFilter}`;
      const { data } = await api.get(`/admin/blog/posts?limit=100${qs}`);
      setPosts(data.posts || []);
      setSelected(new Set()); // clear selection after refresh
      setErr(null);
    } catch (e) {
      const code = e?.response?.status || "error";
      const detail = e?.response?.data?.detail;
      setErr(`HTTP ${code}${detail ? `: ${detail}` : ""}`);
    } finally {
      setLoading(false);
    }
  };

  // Refresh status-counter chips off the health endpoint — cheap call,
  // always reflects DB-wide totals (not just the current page).
  const loadTotals = async () => {
    try {
      const { data } = await api.get("/admin/blog/_health");
      setTotals({
        all: data.total || 0,
        draft: data.draft || 0,
        published: data.published || 0,
      });
    } catch {
      // health endpoint failures shouldn't block the page
    }
  };

  useEffect(() => { load(); }, [statusFilter]);
  useEffect(() => { loadTotals(); }, [posts.length]);

  const drafts = useMemo(() => posts.filter((p) => p.status === "draft"), [posts]);
  const eligibleDrafts = useMemo(
    () => drafts.filter((p) => !(p.content_warnings && p.content_warnings.length)),
    [drafts],
  );
  const skippedByWarnings = drafts.length - eligibleDrafts.length;

  const toggleOne = (id) => {
    setSelected((curr) => {
      const next = new Set(curr);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAllEligible = () => {
    const eligIds = eligibleDrafts.map((p) => p.id);
    const allSelected = eligIds.every((id) => selected.has(id));
    setSelected(allSelected ? new Set() : new Set(eligIds));
  };

  const allEligibleSelected = eligibleDrafts.length > 0 &&
    eligibleDrafts.every((p) => selected.has(p.id));
  const selectedCount = selected.size;

  const toggleStatus = async (p) => {
    const next = p.status === "published" ? "draft" : "published";
    try {
      await api.patch(`/admin/blog/posts/${p.id}/status`, { status: next });
      toast.success(next === "draft"
        ? `Unpublished "${p.title}". Public /blog and sitemap updated.`
        : `Published "${p.title}".`);
      load();
    } catch (e) {
      toast.error(`Failed to update status: HTTP ${e?.response?.status || "error"}`);
    }
  };

  // Phase 2L-A74 — confirmation gate around toggleStatus. Unpublishing a
  // post removes it from /blog + the sitemap immediately, so we ask the
  // admin to confirm. Publishing is also gated (symmetrical UX, same
  // pattern as the bulk-publish modal).
  const [pendingToggle, setPendingToggle] = useState(null);
  const requestToggle = (p) => setPendingToggle(p);
  const confirmToggle = async () => {
    const p = pendingToggle;
    setPendingToggle(null);
    if (!p) return;
    await toggleStatus(p);
  };

  const runBulkPublish = async () => {
    setPublishing(true);
    try {
      const { data } = await api.post("/admin/blog/posts/bulk-publish", {
        ids: Array.from(selected),
      });
      const c = data.counts || {};
      const parts = [];
      if (c.published) parts.push(`✓ ${c.published} published`);
      if (c.skipped_warnings) parts.push(`⚠ ${c.skipped_warnings} held (banned phrases)`);
      if (c.skipped_not_draft) parts.push(`${c.skipped_not_draft} were not drafts`);
      if (c.not_found) parts.push(`${c.not_found} not found`);
      if (c.failed) parts.push(`${c.failed} failed`);
      toast.success(parts.join(" · ") || "No posts changed");
      setConfirming(false);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Bulk publish failed");
    } finally {
      setPublishing(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 p-6 lg:p-10" data-testid="admin-blog-page">
      <header className="max-w-6xl mx-auto mb-6 flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Blog posts</h1>
          <p className="text-sm text-slate-600">
            Imported via GetAutoSEO webhook. Drafts flagged for content review are NOT visible publicly.
          </p>
          {/* Phase 2L-A69 — Draft / Published / All filter tabs. */}
          <div className="mt-2 inline-flex rounded-lg ring-1 ring-slate-200 bg-white overflow-hidden text-xs"
               data-testid="admin-blog-status-tabs" role="tablist">
            {[
              { key: "draft",     label: "Draft",     count: totals.draft },
              { key: "published", label: "Published", count: totals.published },
              { key: "all",       label: "All",       count: totals.all },
            ].map((tab) => {
              const active = statusFilter === tab.key;
              return (
                <button
                  key={tab.key}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => setStatusFilter(tab.key)}
                  data-testid={`admin-blog-tab-${tab.key}`}
                  className={[
                    "px-3 py-1.5 font-medium transition",
                    active
                      ? "bg-slate-900 text-white"
                      : "text-slate-600 hover:bg-slate-50",
                  ].join(" ")}
                >
                  {tab.label}
                  <span className={[
                    "ml-1.5 inline-block min-w-[1.5rem] px-1 py-0 rounded text-[10px] font-semibold",
                    active ? "bg-white/20 text-white" : "bg-slate-100 text-slate-700",
                  ].join(" ")}>{tab.count}</span>
                </button>
              );
            })}
          </div>
          {drafts.length > 0 && (
            <p className="text-xs text-slate-500 mt-1" data-testid="admin-blog-draft-summary">
              {drafts.length} draft{drafts.length === 1 ? "" : "s"} · {eligibleDrafts.length} eligible for bulk publish
              {skippedByWarnings > 0 && ` · ${skippedByWarnings} held by banned-phrase warnings`}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setConfirming(true)}
            disabled={selectedCount === 0 || publishing}
            data-testid="admin-blog-bulk-publish-btn"
            className="px-3.5 py-1.5 text-sm rounded-lg font-semibold bg-emerald-600 text-white hover:bg-emerald-500 disabled:bg-slate-200 disabled:text-slate-400 disabled:cursor-not-allowed transition"
          >
            Publish selected drafts ({selectedCount})
          </button>
          <button
            onClick={load}
            className="px-3 py-1.5 text-sm rounded-lg ring-1 ring-slate-300 hover:bg-white"
            data-testid="admin-blog-refresh"
          >
            Refresh
          </button>
        </div>
      </header>

      {/* Phase 2L-A71 — Admin-only AutoSEO webhook diagnostics. */}
      <AutoSEOWebhookDiagnostics />

      <div className="max-w-6xl mx-auto bg-white ring-1 ring-slate-200 rounded-2xl overflow-hidden">
        {loading && <div className="p-6 text-slate-500">Loading…</div>}
        {err && <div className="p-6 text-red-700" data-testid="admin-blog-error">Error: {err}</div>}
        {!loading && !err && posts.length === 0 && (
          <div className="p-6 text-slate-600" data-testid="admin-blog-empty">No imported posts yet.</div>
        )}
        {!loading && posts.length > 0 && (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="pl-4 pr-2 py-3 w-10">
                  {eligibleDrafts.length > 0 && (
                    <input
                      type="checkbox"
                      checked={allEligibleSelected}
                      onChange={toggleAllEligible}
                      className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                      title="Select all eligible drafts on this page"
                      data-testid="admin-blog-select-all"
                    />
                  )}
                </th>
                <th className="px-4 py-3">Title</th>
                <th className="px-4 py-3">Slug</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Published</th>
                <th className="px-4 py-3">Source</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {posts.map((p) => {
                const isDraft = p.status === "draft";
                const hasWarnings = (p.content_warnings || []).length > 0;
                const checkable = isDraft && !hasWarnings;
                const isSelected = selected.has(p.id);
                return (
                  <tr
                    key={p.id}
                    data-testid={`admin-blog-row-${p.slug}`}
                    className={`hover:bg-slate-50 ${isSelected ? "bg-emerald-50/40" : ""}`}
                  >
                    <td className="pl-4 pr-2 py-3">
                      {checkable ? (
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleOne(p.id)}
                          className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                          data-testid={`admin-blog-checkbox-${p.slug}`}
                        />
                      ) : isDraft && hasWarnings ? (
                        <span title="Held by banned-phrase warnings" className="text-amber-500 text-xs">⚠</span>
                      ) : null}
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-900">{p.title}</div>
                      {hasWarnings && (
                        <div className="mt-1 text-[11px] text-amber-700" data-testid={`warn-${p.slug}`}>
                          Flagged: {p.content_warnings.join(", ")}
                        </div>
                      )}
                      {p.image_warning && (
                        <div className="mt-1 text-[11px] text-amber-700">Image: {p.image_warning}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-slate-600 font-mono text-[12px]">{p.slug}</td>
                    <td className="px-4 py-3">
                      <span className={[
                        "inline-block px-2 py-0.5 rounded-full text-[11px] font-semibold",
                        p.status === "published" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800",
                      ].join(" ")}>{p.status}</span>
                    </td>
                    <td className="px-4 py-3 text-slate-600">{fmtDate(p.published_at)}</td>
                    <td className="px-4 py-3 text-slate-600">{p.source || "—"}</td>
                    <td className="px-4 py-3 text-right space-x-3">
                      <a href={`/blog/${p.slug}`} target="_blank" rel="noopener noreferrer"
                         className="text-[#1F4E79] hover:underline" data-testid={`view-${p.slug}`}>View</a>
                      <button onClick={() => requestToggle(p)}
                              className={[
                                "hover:underline",
                                p.status === "published" ? "text-rose-700" : "text-emerald-700",
                              ].join(" ")}
                              data-testid={`toggle-${p.slug}`}>
                        {p.status === "published" ? "Unpublish" : "Publish"}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Phase 2L-A74 — per-row publish/unpublish confirmation. */}
      {pendingToggle && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4"
          data-testid="admin-blog-toggle-modal"
        >
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
            {pendingToggle.status === "published" ? (
              <>
                <h2 className="text-lg font-bold text-slate-900">
                  Unpublish this article?
                </h2>
                <p className="text-sm text-slate-600 mt-2 leading-relaxed">
                  <span className="font-semibold text-slate-900">{pendingToggle.title}</span>
                </p>
                <ul className="mt-3 text-xs text-slate-600 space-y-1 list-disc pl-5">
                  <li>Removed from the public <code className="bg-slate-100 px-1 rounded">/blog</code> listing immediately.</li>
                  <li>Direct <code className="bg-slate-100 px-1 rounded">/blog/{pendingToggle.slug}</code> URL will return 404.</li>
                  <li>Excluded from <code className="bg-slate-100 px-1 rounded">sitemap.xml</code>.</li>
                  <li>Kept here as a draft — you can re-publish later in one click.</li>
                </ul>
              </>
            ) : (
              <>
                <h2 className="text-lg font-bold text-slate-900">
                  Publish this draft?
                </h2>
                <p className="text-sm text-slate-600 mt-2 leading-relaxed">
                  <span className="font-semibold text-slate-900">{pendingToggle.title}</span>
                </p>
                <p className="mt-3 text-xs text-slate-600">
                  The article will appear on the public <code className="bg-slate-100 px-1 rounded">/blog</code>
                  {" "}listing and the sitemap immediately.
                </p>
                {(pendingToggle.content_warnings || []).length > 0 && (
                  <div className="mt-3 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800">
                    Held by banned-phrase warning(s): {pendingToggle.content_warnings.join(", ")}
                  </div>
                )}
              </>
            )}
            <div className="mt-5 flex justify-end gap-2">
              <button
                onClick={() => setPendingToggle(null)}
                data-testid="admin-blog-toggle-cancel"
                className="px-3.5 py-1.5 text-sm rounded-lg ring-1 ring-slate-300 hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmToggle}
                data-testid="admin-blog-toggle-confirm"
                className={[
                  "px-3.5 py-1.5 text-sm rounded-lg font-semibold text-white",
                  pendingToggle.status === "published"
                    ? "bg-rose-600 hover:bg-rose-500"
                    : "bg-emerald-600 hover:bg-emerald-500",
                ].join(" ")}
              >
                {pendingToggle.status === "published" ? "Unpublish" : "Publish"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation modal */}
      {confirming && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4" data-testid="admin-blog-confirm-modal">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
            <h2 className="text-lg font-bold text-slate-900">Publish {selectedCount} draft{selectedCount === 1 ? "" : "s"}?</h2>
            <p className="text-sm text-slate-600 mt-2 leading-relaxed">
              Selected drafts will become publicly visible on <code className="bg-slate-100 px-1 rounded">/blog</code> and the sitemap immediately.
            </p>
            <div className="mt-3 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800">
              <strong>Posts with banned-phrase warnings will be skipped</strong> and remain draft. Those rows
              aren&apos;t selectable from this screen — you&apos;ll see the count in the result.
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button
                onClick={() => setConfirming(false)}
                disabled={publishing}
                data-testid="admin-blog-confirm-cancel"
                className="px-3.5 py-1.5 text-sm rounded-lg ring-1 ring-slate-300 hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                onClick={runBulkPublish}
                disabled={publishing}
                data-testid="admin-blog-confirm-publish"
                className="px-3.5 py-1.5 text-sm rounded-lg font-semibold bg-emerald-600 text-white hover:bg-emerald-500 disabled:bg-emerald-300"
              >
                {publishing ? "Publishing…" : `Publish ${selectedCount}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
